import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/eabq3wxl_FINRUN%20LOGO%20PNG%202.png";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle navigation to sections
  const handleSectionClick = (e, href) => {
    e.preventDefault();
    
    // Check if it's a hash link to homepage section
    if (href.startsWith("/#")) {
      const sectionId = href.replace("/#", "");
      
      if (location.pathname === "/") {
        // Already on homepage, just scroll
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // Navigate to homepage first, then scroll after page loads
        navigate("/");
        // Use requestAnimationFrame for better timing
        const scrollToSection = () => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          } else {
            // Retry if element not found yet
            requestAnimationFrame(scrollToSection);
          }
        };
        setTimeout(scrollToSection, 300);
      }
    } else {
      // Regular page navigation
      navigate(href);
    }
  };

  const footerLinks = {
    "Quick Links": [
      { label: "Home", href: "/" },
      { label: "Register", href: "/register" },
      { label: "Sponsors", href: "/sponsor" },
      { label: "Gallery", href: "/gallery/bengaluru-2025" }
    ],
    "Events": [
      { label: "Hyderabad 2027", href: "/register/hyderabad-2027" },
      { label: "Past Events", href: "/#past-events" }
    ],
    "Support": [
      { label: "FAQ", href: "/#faq" },
      { label: "Contact Us", href: "/#contact" },
      { label: "About Us", href: "/#about" },
      { label: "Pricing", href: "/#pricing" }
    ]
  };

  // Social media links
  const socialLinks = [
    { name: "Instagram", abbr: "Ig", url: "https://www.instagram.com/thefinrun/" },
    { name: "Facebook", abbr: "Fb", url: "https://www.facebook.com/people/The-FIN-RUN/61564768154937/" },
    { name: "LinkedIn", abbr: "Li", url: "https://www.linkedin.com/company/105964263/" }
  ];

  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="footer">
      {/* Big Logo */}
      <div className="border-b border-white/10">
        <div className="container-custom py-12">
          <Link to="/" onClick={scrollToTop}>
            <img
              src={LOGO_URL}
              alt="The FinRun"
              className="h-40 md:h-52 w-auto mx-auto cursor-pointer hover:opacity-90 transition-opacity"
            />
          </Link>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container-custom py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" onClick={scrollToTop}>
              <img
                src={LOGO_URL}
                alt="The FinRun"
                className="h-24 md:h-32 w-auto mb-6 cursor-pointer hover:opacity-90 transition-opacity"
              />
            </Link>
            <p className="text-gray-400 mb-6 max-w-sm">
              India's First & Largest Cyber Awareness Run. Stride Against Cyber Crimes - 
              building a national movement where every runner becomes an advocate for digital safety.
            </p>
            <div className="flex items-center gap-4 mb-8">
              <a
                href="mailto:info@thefinrun.com"
                className="text-[#FFD700] hover:text-[#FF3B30] transition-colors"
              >
                info@thefinrun.com
              </a>
            </div>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={social.name}
                  className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center text-gray-400 hover:bg-[#FF3B30] hover:text-white transition-all"
                >
                  <span className="text-xs font-bold">{social.abbr}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-bold text-white mb-6">{title}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      onClick={(e) => handleSectionClick(e, link.href)}
                      className="text-gray-400 hover:text-[#FF3B30] transition-colors cursor-pointer"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © 2026 The FinRun. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <p className="text-gray-500 text-sm">
                A cyber awareness initiative
              </p>
              <button
                onClick={scrollToTop}
                className="w-10 h-10 bg-[#FF3B30] rounded-lg flex items-center justify-center hover:bg-[#FF1C0F] transition-colors"
                data-testid="scroll-to-top-btn"
                title="Back to top"
              >
                <ArrowUp className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
